import { z } from "zod"

export enum EBusinessReportKind {
  EXECUTIVE = "executive",
  ACQUISITION = "acquisition",
  REVENUE = "revenue",
  ENGINEERING = "engineering",
}

export enum EReportingDomain {
  SEARCH = "search",
  MARKETING = "marketing",
  BILLING = "billing",
  ENGINEERING = "engineering",
  OPERATIONS = "operations",
}

export enum EBusinessReportModuleStatus {
  AVAILABLE = "available",
  PARTIAL = "partial",
  STALE = "stale",
  UNAVAILABLE = "unavailable",
}

export const BUSINESS_REPORT_SCHEMA_VERSION = 2 as const
export const BUSINESS_REPORT_INTERPRETATION_VERSION = "business-reporting-v1" as const

export interface BusinessReportWindow {
  start: string
  end: string
}

export interface BusinessReportEvidence {
  requestedWindow: BusinessReportWindow
  actualWindow: BusinessReportWindow | null
  metricDefinitionVersion: string
  units: string[]
  denominators: string[]
  collectedAt: string | null
  sourceCutoff: string | null
  coverage: { expected: number | null; observed: number | null; reconciled: boolean | null; detail: string }
  provenance: { sourceIds: string[]; collectionIds: string[]; digests: string[] }
}

interface BusinessReportModuleBase {
  status: EBusinessReportModuleStatus
  reason: string | null
  evidence: BusinessReportEvidence
}

export interface ReportingSearchModule extends BusinessReportModuleBase {
  domain: EReportingDomain.SEARCH
  data: {
    products: Array<{
      productKey: string
      clicks: number | null
      impressions: number | null
      observedDays: number
      expectedDays: number
    }>
  } | null
}

export interface ReportingMarketingModule extends BusinessReportModuleBase {
  domain: EReportingDomain.MARKETING
  data: {
    products: Array<{
      productKey: string
      provider: string
      providerAccountId: string
      environment: string
      livemode: boolean
      currency: string
      spendMinor: number | null
      providerConversions: number | null
      attributedTrials: number | null
      attributedPurchases: number | null
      attributionModel: string
      reconciliationStatus: string
    }>
  } | null
}

export interface ReportingBillingModule extends BusinessReportModuleBase {
  domain: EReportingDomain.BILLING
  data: {
    cohorts: Array<{
      productKey: string
      provider: string
      environment: string
      stripeAccountKey: string
      livemode: boolean
      currency: string
      cohort: string
      subjects: number
      convertedSubjects: number
      conversionRate: number | null
      grossRevenueMinor: number | null
      refundsMinor: number | null
      netRevenueMinor: number | null
      state: "observed" | "unknown"
    }>
    retention: Array<{
      productKey: string
      provider: string
      environment: string
      stripeAccountKey: string
      livemode: boolean
      currency: string
      cohort: string
      horizonDays: number
      eligibleSubjects: number
      retainedSubjects: number
      unknownSubjects: number
      retentionRate: number | null
      matured: boolean
      state: "observed" | "unknown"
    }>
  } | null
}

export interface ReportingEngineeringModule extends BusinessReportModuleBase {
  domain: EReportingDomain.ENGINEERING
  data: {
    products: Array<{
      scope: "company" | "product"
      productKey: string | null
      repositoryIds: string[]
      mergedPullRequests: number | null
      medianReadyToMergeHours: number | null
      reviewCoverageRate: number | null
      ciAttempts: number | null
      ciSuccessfulAttempts: number | null
      deploymentCount: number | null
    }>
  } | null
}

export interface ReportingOperationsModule extends BusinessReportModuleBase {
  domain: EReportingDomain.OPERATIONS
  data: {
    products: Array<{
      productKey: string
      checkExceptions: number | null
      openIncidents: number | null
      failedDeployments: number | null
      detail: string
    }>
  } | null
}

export type BusinessReportModule =
  | ReportingSearchModule
  | ReportingMarketingModule
  | ReportingBillingModule
  | ReportingEngineeringModule
  | ReportingOperationsModule

export interface BusinessReportInput {
  schemaVersion: typeof BUSINESS_REPORT_SCHEMA_VERSION
  companyId: string
  reportKind: EBusinessReportKind
  definitionVersion: number
  interpretationRuleVersion: string
  productKeys: string[]
  period: BusinessReportWindow & { timezone: string }
  generatedAt: string
  modules: BusinessReportModule[]
}

export interface BusinessReportCompositionRequest {
  companyId: string
  reportKind: EBusinessReportKind
  definitionVersion: number
  productKeys: string[]
  period: BusinessReportWindow & { timezone: string }
}

export interface BusinessReportSnapshotRequest extends BusinessReportCompositionRequest {
  editionId: string
}

export interface ReportingDomainBinding {
  companyId: string
  productId: string | null
  productKey: string | null
  sharedCompanyScope: boolean
  domain: EReportingDomain
  provider: string
  providerAccountId: string
  environment: string
  livemode: boolean | null
  currency: string | null
  includedFrom: string
  active: boolean
  verifiedBy: string
  verifiedAt: string
  revokedAt: string | null
}

const keySchema = z.string().trim().min(1).max(200)
const MAX_REPORT_DAYS = 120
const DAY_MS = 86_400_000
const dateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((value) => {
    const parsed = new Date(`${value}T00:00:00.000Z`)
    return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value
  }, "Invalid calendar date")
const timestampSchema = z.string().datetime()
const nullableMetric = z.number().finite().nullable()

const businessReportWindowShape = { start: dateSchema, end: dateSchema } as const

const refineWindow = <T extends { start: string; end: string }>(value: T, ctx: z.RefinementCtx) => {
  const days = Math.floor((Date.parse(value.end) - Date.parse(value.start)) / DAY_MS) + 1
  if (days < 1 || days > MAX_REPORT_DAYS)
    ctx.addIssue({ code: "custom", message: "Report window must contain 1-120 inclusive days" })
}

export const businessReportWindowSchema = z.object(businessReportWindowShape).strict().superRefine(refineWindow)

export const businessReportPeriodSchema = z
  .object({ ...businessReportWindowShape, timezone: keySchema })
  .strict()
  .superRefine(refineWindow)

export const businessReportEvidenceSchema = z
  .object({
    requestedWindow: businessReportWindowSchema,
    actualWindow: businessReportWindowSchema.nullable(),
    metricDefinitionVersion: keySchema,
    units: z.array(keySchema).min(1).max(20),
    denominators: z.array(keySchema).max(20),
    collectedAt: timestampSchema.nullable(),
    sourceCutoff: timestampSchema.nullable(),
    coverage: z
      .object({
        expected: z.number().int().nonnegative().nullable(),
        observed: z.number().int().nonnegative().nullable(),
        reconciled: z.boolean().nullable(),
        detail: z.string().trim().min(1).max(1_000),
      })
      .strict(),
    provenance: z
      .object({
        sourceIds: z.array(keySchema).max(200),
        collectionIds: z.array(keySchema).max(2_000),
        digests: z.array(keySchema).max(200),
      })
      .strict(),
  })
  .strict()

const moduleStatusSchema = z.nativeEnum(EBusinessReportModuleStatus)
const moduleBaseShape = {
  status: moduleStatusSchema,
  reason: z.string().trim().min(1).max(1_000).nullable(),
  evidence: businessReportEvidenceSchema,
} as const

const searchProductSchema = z
  .object({
    productKey: keySchema,
    clicks: nullableMetric,
    impressions: nullableMetric,
    observedDays: z.number().int().nonnegative(),
    expectedDays: z.number().int().positive(),
  })
  .strict()

const marketingProductSchema = z
  .object({
    productKey: keySchema,
    provider: keySchema,
    providerAccountId: keySchema,
    environment: keySchema,
    livemode: z.boolean(),
    currency: z
      .string()
      .trim()
      .length(3)
      .transform((value) => value.toLowerCase()),
    spendMinor: nullableMetric,
    providerConversions: nullableMetric,
    attributedTrials: nullableMetric,
    attributedPurchases: nullableMetric,
    attributionModel: keySchema,
    reconciliationStatus: keySchema,
  })
  .strict()

const billingCohortSchema = z
  .object({
    productKey: keySchema,
    provider: keySchema,
    environment: keySchema,
    stripeAccountKey: keySchema,
    livemode: z.boolean(),
    currency: z
      .string()
      .trim()
      .length(3)
      .transform((value) => value.toLowerCase()),
    cohort: keySchema,
    subjects: z.number().int().nonnegative(),
    convertedSubjects: z.number().int().nonnegative(),
    conversionRate: nullableMetric,
    grossRevenueMinor: nullableMetric,
    refundsMinor: nullableMetric,
    netRevenueMinor: nullableMetric,
    state: z.enum(["observed", "unknown"]),
  })
  .strict()

const billingRetentionSchema = z
  .object({
    productKey: keySchema,
    provider: keySchema,
    environment: keySchema,
    stripeAccountKey: keySchema,
    livemode: z.boolean(),
    currency: z
      .string()
      .trim()
      .length(3)
      .transform((value) => value.toLowerCase()),
    cohort: keySchema,
    horizonDays: z.number().int().positive(),
    eligibleSubjects: z.number().int().nonnegative(),
    retainedSubjects: z.number().int().nonnegative(),
    unknownSubjects: z.number().int().nonnegative(),
    retentionRate: nullableMetric,
    matured: z.boolean(),
    state: z.enum(["observed", "unknown"]),
  })
  .strict()

const engineeringProductSchema = z
  .object({
    scope: z.enum(["company", "product"]),
    productKey: keySchema.nullable(),
    repositoryIds: z.array(z.string().regex(/^\d+$/)).min(1).max(100),
    mergedPullRequests: nullableMetric,
    medianReadyToMergeHours: nullableMetric,
    reviewCoverageRate: nullableMetric,
    ciAttempts: nullableMetric,
    ciSuccessfulAttempts: nullableMetric,
    deploymentCount: nullableMetric,
  })
  .strict()
  .superRefine((value, ctx) => {
    if ((value.scope === "company") !== (value.productKey === null))
      ctx.addIssue({ code: "custom", message: "Engineering summary scope and product key disagree" })
  })

const operationsProductSchema = z
  .object({
    productKey: keySchema,
    checkExceptions: nullableMetric,
    openIncidents: nullableMetric,
    failedDeployments: nullableMetric,
    detail: z.string().trim().min(1).max(1_000),
  })
  .strict()

const moduleInvariant = (
  value: { status: EBusinessReportModuleStatus; reason: string | null; data: unknown },
  ctx: z.RefinementCtx,
) => {
  const { status, reason, data } = value
  if (status === EBusinessReportModuleStatus.AVAILABLE && (reason !== null || data === null))
    ctx.addIssue({ code: "custom", message: "Available modules require data and no failure reason" })
  if (status !== EBusinessReportModuleStatus.AVAILABLE && !reason)
    ctx.addIssue({ code: "custom", message: "Incomplete modules require a reason" })
}

export const reportingSearchModuleSchema = z
  .object({
    domain: z.literal(EReportingDomain.SEARCH),
    ...moduleBaseShape,
    data: z
      .object({ products: z.array(searchProductSchema).max(100) })
      .strict()
      .nullable(),
  })
  .strict()
  .superRefine(moduleInvariant)

export const reportingMarketingModuleSchema = z
  .object({
    domain: z.literal(EReportingDomain.MARKETING),
    ...moduleBaseShape,
    data: z
      .object({ products: z.array(marketingProductSchema).max(500) })
      .strict()
      .nullable(),
  })
  .strict()
  .superRefine(moduleInvariant)

export const reportingBillingModuleSchema = z
  .object({
    domain: z.literal(EReportingDomain.BILLING),
    ...moduleBaseShape,
    data: z
      .object({
        cohorts: z.array(billingCohortSchema).max(2_000),
        retention: z.array(billingRetentionSchema).max(5_000),
      })
      .strict()
      .nullable(),
  })
  .strict()
  .superRefine(moduleInvariant)

export const reportingEngineeringModuleSchema = z
  .object({
    domain: z.literal(EReportingDomain.ENGINEERING),
    ...moduleBaseShape,
    data: z
      .object({ products: z.array(engineeringProductSchema).max(100) })
      .strict()
      .nullable(),
  })
  .strict()
  .superRefine(moduleInvariant)

export const reportingOperationsModuleSchema = z
  .object({
    domain: z.literal(EReportingDomain.OPERATIONS),
    ...moduleBaseShape,
    data: z
      .object({ products: z.array(operationsProductSchema).max(100) })
      .strict()
      .nullable(),
  })
  .strict()
  .superRefine(moduleInvariant)

export const businessReportModuleSchema = z.union([
  reportingSearchModuleSchema,
  reportingMarketingModuleSchema,
  reportingBillingModuleSchema,
  reportingEngineeringModuleSchema,
  reportingOperationsModuleSchema,
])

export const businessReportInputSchema = z
  .object({
    schemaVersion: z.literal(BUSINESS_REPORT_SCHEMA_VERSION),
    companyId: z.string().uuid(),
    reportKind: z.nativeEnum(EBusinessReportKind),
    definitionVersion: z.number().int().positive(),
    interpretationRuleVersion: keySchema,
    productKeys: z.array(keySchema).min(1).max(100),
    period: businessReportPeriodSchema,
    generatedAt: timestampSchema,
    modules: z.array(businessReportModuleSchema).max(5),
  })
  .strict()
  .superRefine((value, ctx) => {
    const sortedProducts = [...new Set(value.productKeys)].sort()
    if (
      sortedProducts.length !== value.productKeys.length ||
      sortedProducts.some((key, index) => key !== value.productKeys[index])
    )
      ctx.addIssue({ code: "custom", message: "Product scope must be sorted and unique" })
    const domains = value.modules.map((module) => module.domain)
    if (new Set(domains).size !== domains.length)
      ctx.addIssue({ code: "custom", message: "Report modules must have unique domains" })
    const definition = BUSINESS_REPORT_DEFINITIONS[value.reportKind]
    if (!definition || definition.version !== value.definitionVersion)
      ctx.addIssue({ code: "custom", message: "Unsupported business report definition" })
    const expectedDomains = definition?.modules.map((entry) => entry.domain) ?? []
    if (domains.length !== expectedDomains.length || domains.some((domain, index) => domain !== expectedDomains[index]))
      ctx.addIssue({ code: "custom", message: "Report modules must exactly match the selected definition" })
  })

export const businessReportCompositionRequestSchema = z
  .object({
    companyId: z.string().uuid(),
    reportKind: z.nativeEnum(EBusinessReportKind),
    definitionVersion: z.number().int().positive().default(1),
    productKeys: z.array(keySchema).min(1).max(100),
    period: businessReportPeriodSchema,
  })
  .strict()

export const businessReportSnapshotRequestSchema = businessReportCompositionRequestSchema
  .extend({ editionId: z.string().uuid() })
  .strict()

export const BUSINESS_REPORT_DEFINITIONS = {
  [EBusinessReportKind.EXECUTIVE]: {
    version: 1,
    minimumAvailable: 1,
    modules: [
      { domain: EReportingDomain.SEARCH, required: false },
      { domain: EReportingDomain.MARKETING, required: false },
      { domain: EReportingDomain.BILLING, required: false },
      { domain: EReportingDomain.ENGINEERING, required: false },
      { domain: EReportingDomain.OPERATIONS, required: false },
    ],
  },
  [EBusinessReportKind.ACQUISITION]: {
    version: 1,
    modules: [
      { domain: EReportingDomain.SEARCH, required: false },
      { domain: EReportingDomain.MARKETING, required: false },
    ],
    minimumAvailable: 1,
  },
  [EBusinessReportKind.REVENUE]: {
    version: 1,
    modules: [{ domain: EReportingDomain.BILLING, required: true }],
  },
  [EBusinessReportKind.ENGINEERING]: {
    version: 1,
    modules: [
      { domain: EReportingDomain.ENGINEERING, required: true },
      { domain: EReportingDomain.OPERATIONS, required: false },
    ],
  },
} as const

const bindingBaseShape = {
  companyId: z.string().uuid(),
  productId: z.string().uuid().nullable(),
  productKey: keySchema.nullable(),
  sharedCompanyScope: z.boolean(),
  provider: keySchema,
  providerAccountId: keySchema,
  environment: keySchema,
  livemode: z.boolean().nullable(),
  currency: z
    .string()
    .trim()
    .length(3)
    .transform((value) => value.toLowerCase())
    .nullable(),
  includedFrom: timestampSchema,
  active: z.boolean(),
  verifiedBy: z.string().email(),
  verifiedAt: timestampSchema,
  revokedAt: timestampSchema.nullable(),
} as const

const makeBindingSchema = <T extends EReportingDomain>(domain: T) =>
  z
    .object({ domain: z.literal(domain), ...bindingBaseShape })
    .strict()
    .superRefine((value, ctx) => {
      if (value.sharedCompanyScope === (value.productId !== null || value.productKey !== null))
        ctx.addIssue({ code: "custom", message: "Binding must use one product scope or explicit shared-company scope" })
      if (value.active === (value.revokedAt !== null))
        ctx.addIssue({ code: "custom", message: "Active and revoked binding state disagree" })
      if (
        domain === EReportingDomain.BILLING &&
        (value.provider !== "stripe" || value.livemode === null || value.currency === null)
      )
        ctx.addIssue({ code: "custom", message: "Billing bindings require Stripe account, mode and currency" })
      if (domain === EReportingDomain.MARKETING && value.livemode === null)
        ctx.addIssue({ code: "custom", message: "Marketing bindings require an exact mode" })
      if (domain === EReportingDomain.ENGINEERING && !/^\d+$/.test(value.providerAccountId))
        ctx.addIssue({ code: "custom", message: "Engineering bindings require a numeric repository ID" })
    })

export const reportingDomainBindingSchema = z.union([
  makeBindingSchema(EReportingDomain.SEARCH),
  makeBindingSchema(EReportingDomain.MARKETING),
  makeBindingSchema(EReportingDomain.BILLING),
  makeBindingSchema(EReportingDomain.ENGINEERING),
  makeBindingSchema(EReportingDomain.OPERATIONS),
])
